import config from 'app-config'
import { TIME_RANGES } from 'core/components/widgets/TimePicklist'
import { isDevelopment, isLocalhost } from 'core/utils/helpers'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Metrics API Keys
export const METRICS_API_KEYS = {
  // All Hosts
  ALL_HOSTS_CPU_USAGE: 'all_hosts_cpu_usage',
  ALL_HOSTS_MEMORY_USAGE: 'all_hosts_memory_usage',
  ALL_HOSTS_DISK_USAGE: 'all_hosts_disk_usage',
  ALL_HOSTS_NETWORK_RX_THROUGHPUT: 'host_network_rx_throughput',
  ALL_HOSTS_NETWORK_TX_THROUGHPUT: 'host_network_tx_throughput',

  // Selected Host
  SELECTED_HOST_CPU_USAGE: 'selected_host_cpu_usage',
  SELECTED_HOST_MEMORY_USAGE: 'selected_host_memory_usage',
  SELECTED_HOST_DISK_USAGE: 'selected_host_disk_usage',

  // Per Host
  PER_HOST_CPU_USAGE: 'per_host_cpu_usage',
  PER_HOST_MEMORY_USAGE: 'per_host_memory_usage',
  PER_HOST_DISK_USAGE: 'per_host_disk_usage',
  PER_HOST_NETWORK_RX_THROUGHPUT: 'per_host_network_rx_throughput',
  PER_HOST_NETWORK_TX_THROUGHPUT: 'per_host_network_tx_throughput',
  PER_HOST_DISK_READ_THROUGHPUT: 'per_host_disk_read_throughput',
  PER_HOST_DISK_WRITE_THROUGHPUT: 'per_host_disk_write_throughput',
  PER_HOST_DISK_READ_IOPS: 'per_host_disk_read_iops',
  PER_HOST_DISK_WRITE_IOPS: 'per_host_disk_write_iops',

  // All VMs
  ALL_VMS_CPU_USAGE: 'all_vm_cpu_usage',
  ALL_VMS_MEMORY_USAGE: 'all_vm_memory_usage',
  ALL_VMS_CPU_THROTTLE: 'all_vm_cpu_throttling_percentage',
  ALL_VMS_DISK_READ_THROUGHPUT: 'all_vm_disk_read_throughput',
  ALL_VMS_DISK_WRITE_THROUGHPUT: 'all_vm_disk_write_throughput',
  ALL_VMS_DISK_READ_IOPS: 'all_vm_disk_read_iops',
  ALL_VMS_DISK_WRITE_IOPS: 'all_vm_disk_write_iops',
  ALL_VMS_NETWORK_RX_THROUGHPUT: 'all_vm_network_rx_throughput',
  ALL_VMS_NETWORK_TX_THROUGHPUT: 'all_vm_network_tx_throughput',

  // Selected VM
  SELECTED_VMS_CPU_USAGE: 'selected_vm_cpu_usage',
  SELECTED_VMS_MEMORY_USAGE: 'selected_vm_memory_usage',
  SELECTED_VMS_CPU_THROTTLE: 'selected_vm_cpu_throttling_percentage',
  SELECTED_VMS_DISK_READ_THROUGHPUT: 'selected_vm_disk_read_throughput',
  SELECTED_VMS_DISK_WRITE_THROUGHPUT: 'selected_vm_disk_write_throughput',
  SELECTED_VMS_DISK_READ_IOPS: 'selected_vm_disk_read_iops',
  SELECTED_VMS_DISK_WRITE_IOPS: 'selected_vm_disk_write_iops',
  SELECTED_VMS_NETWORK_RX_THROUGHPUT: 'selected_vm_network_rx_throughput',
  SELECTED_VMS_NETWORK_TX_THROUGHPUT: 'selected_vm_network_tx_throughput',

  // Per VM
  PER_VM_CPU_USAGE: 'per_vm_cpu_usage',
  PER_VM_MEMORY_USAGE: 'per_vm_memory_usage',
  PER_VM_CPU_THROTTLE: 'per_vm_cpu_throttle',
  PER_VM_DISK_READ_THROUGHPUT: 'per_vm_disk_read_throughput',
  PER_VM_DISK_WRITE_THROUGHPUT: 'per_vm_disk_write_throughput',
  PER_VM_DISK_READ_IOPS: 'per_vm_disk_read_iops',
  PER_VM_DISK_WRITE_IOPS: 'per_vm_disk_write_iops',
  PER_VM_NETWORK_RX_THROUGHPUT: 'per_vm_network_rx_throughput',
  PER_VM_NETWORK_TX_THROUGHPUT: 'per_vm_network_tx_throughput',
}

export const SHOW_FOR_SELECTED_RESOURCE_KEYS = [
  METRICS_API_KEYS.SELECTED_HOST_CPU_USAGE,
  METRICS_API_KEYS.SELECTED_HOST_MEMORY_USAGE,
  METRICS_API_KEYS.SELECTED_HOST_DISK_USAGE,
  METRICS_API_KEYS.SELECTED_VMS_CPU_USAGE,
  METRICS_API_KEYS.SELECTED_VMS_MEMORY_USAGE,
  METRICS_API_KEYS.SELECTED_VMS_CPU_THROTTLE,
  METRICS_API_KEYS.SELECTED_VMS_DISK_READ_THROUGHPUT,
  METRICS_API_KEYS.SELECTED_VMS_DISK_WRITE_THROUGHPUT,
  METRICS_API_KEYS.SELECTED_VMS_DISK_READ_IOPS,
  METRICS_API_KEYS.SELECTED_VMS_DISK_WRITE_IOPS,
  METRICS_API_KEYS.SELECTED_VMS_NETWORK_RX_THROUGHPUT,
  METRICS_API_KEYS.SELECTED_VMS_NETWORK_TX_THROUGHPUT,
]

// Metrics Endpoints
export const METRICS_ENDPOINTS = {
  // Hosts
  [METRICS_API_KEYS.ALL_HOSTS_CPU_USAGE]: 'metrics/api/v1/query_range?query=sum(pcd:hyp_cpu_usage)',
  [METRICS_API_KEYS.ALL_HOSTS_MEMORY_USAGE]:
    'metrics/api/v1/query_range?query=sum(pcd:hyp_mem_usage)',
  [METRICS_API_KEYS.ALL_HOSTS_DISK_USAGE]:
    'metrics/api/v1/query_range?query=sum(pcd:hyp_disk_usage{fstype="ext4"})',
  [METRICS_API_KEYS.ALL_HOSTS_NETWORK_RX_THROUGHPUT]:
    'metrics/api/v1/query_range?query=sum(pcd:hyp_net_rx_throughput)',
  [METRICS_API_KEYS.ALL_HOSTS_NETWORK_TX_THROUGHPUT]:
    'metrics/api/v1/query_range?query=sum(pcd:hyp_net_tx_throughput)',

  // Selected Host
  [METRICS_API_KEYS.SELECTED_HOST_CPU_USAGE]: 'metrics/api/v1/query_range?query=pcd:hyp_cpu_usage',
  [METRICS_API_KEYS.SELECTED_HOST_MEMORY_USAGE]:
    'metrics/api/v1/query_range?query=pcd:hyp_mem_usage',
  [METRICS_API_KEYS.SELECTED_HOST_DISK_USAGE]:
    'metrics/api/v1/query_range?query=pcd:hyp_disk_usage{fstype="ext4"}',

  // Per Host
  [METRICS_API_KEYS.PER_HOST_NETWORK_RX_THROUGHPUT]:
    'metrics/api/v1/query_range?query=pcd:hyp_net_rx_throughput{host="{{id}}"}',
  [METRICS_API_KEYS.PER_HOST_NETWORK_TX_THROUGHPUT]:
    'metrics/api/v1/query_range?query=pcd:hyp_net_tx_throughput{host="{{id}}"}',

  [METRICS_API_KEYS.PER_HOST_CPU_USAGE]:
    'metrics/api/v1/query_range?query=pcd:hyp_cpu_usage{host="{{id}}"}',
  [METRICS_API_KEYS.PER_HOST_MEMORY_USAGE]:
    'metrics/api/v1/query_range?query=pcd:hyp_mem_usage{host="{{id}}"}',
  [METRICS_API_KEYS.PER_HOST_DISK_USAGE]:
    'metrics/api/v1/query_range?query=pcd:hyp_disk_usage{host="{{id}}"}',
  [METRICS_API_KEYS.PER_HOST_DISK_READ_THROUGHPUT]:
    'metrics/api/v1/query_range?query=sum(pcd:vm_read_throughput{host="{{id}}"})',
  [METRICS_API_KEYS.PER_HOST_DISK_WRITE_THROUGHPUT]:
    'metrics/api/v1/query_range?query=sum(pcd:vm_write_throughput{host="{{id}}"})',
  [METRICS_API_KEYS.PER_HOST_DISK_READ_IOPS]:
    'metrics/api/v1/query_range?query=sum(pcd:vm_read_iops{host="{{id}}"})',
  [METRICS_API_KEYS.PER_HOST_DISK_WRITE_IOPS]:
    'metrics/api/v1/query_range?query=sum(pcd:vm_write_iops{host="{{id}}"})',

  // All VMS
  [METRICS_API_KEYS.ALL_VMS_CPU_USAGE]: 'metrics/api/v1/query_range?query=sum(pcd:vm_cpu_usage)',
  [METRICS_API_KEYS.ALL_VMS_MEMORY_USAGE]: 'metrics/api/v1/query_range?query=sum(pcd:vm_mem_usage)',
  [METRICS_API_KEYS.ALL_VMS_CPU_THROTTLE]:
    'metrics/api/v1/query_range?query=sum(pcd:vm_cpu_throttling_percentage)',
  [METRICS_API_KEYS.ALL_VMS_DISK_READ_THROUGHPUT]:
    'metrics/api/v1/query_range?query=sum(pcd:vm_read_throughput)',
  [METRICS_API_KEYS.ALL_VMS_DISK_WRITE_THROUGHPUT]:
    'metrics/api/v1/query_range?query=sum(pcd:vm_write_throughput)',
  [METRICS_API_KEYS.ALL_VMS_DISK_READ_IOPS]:
    'metrics/api/v1/query_range?query=sum(pcd:vm_read_iops)',
  [METRICS_API_KEYS.ALL_VMS_DISK_WRITE_IOPS]:
    'metrics/api/v1/query_range?query=sum(pcd:vm_write_iops)',
  [METRICS_API_KEYS.ALL_VMS_NETWORK_RX_THROUGHPUT]:
    'metrics/api/v1/query_range?query=sum(pcd:vm_rx_bytes)',
  [METRICS_API_KEYS.ALL_VMS_NETWORK_TX_THROUGHPUT]:
    'metrics/api/v1/query_range?query=sum(pcd:vm_tx_bytes)',

  // Selected VMs
  [METRICS_API_KEYS.SELECTED_VMS_CPU_USAGE]: 'metrics/api/v1/query_range?query=pcd:vm_cpu_usage',
  [METRICS_API_KEYS.SELECTED_VMS_MEMORY_USAGE]: 'metrics/api/v1/query_range?query=pcd:vm_mem_usage',
  [METRICS_API_KEYS.SELECTED_VMS_CPU_THROTTLE]:
    'metrics/api/v1/query_range?query=pcd:vm_cpu_throttling_percentage',
  [METRICS_API_KEYS.SELECTED_VMS_DISK_READ_THROUGHPUT]:
    'metrics/api/v1/query_range?query=pcd:vm_read_throughput',
  [METRICS_API_KEYS.SELECTED_VMS_DISK_WRITE_THROUGHPUT]:
    'metrics/api/v1/query_range?query=pcd:vm_write_throughput',
  [METRICS_API_KEYS.SELECTED_VMS_DISK_READ_IOPS]:
    'metrics/api/v1/query_range?query=pcd:vm_read_iops',
  [METRICS_API_KEYS.SELECTED_VMS_DISK_WRITE_IOPS]:
    'metrics/api/v1/query_range?query=pcd:vm_write_iops',
  [METRICS_API_KEYS.SELECTED_VMS_NETWORK_RX_THROUGHPUT]:
    'metrics/api/v1/query_range?query=pcd:vm_rx_bytes',
  [METRICS_API_KEYS.SELECTED_VMS_NETWORK_TX_THROUGHPUT]:
    'metrics/api/v1/query_range?query=pcd:vm_tx_bytes',

  // Per VM
  [METRICS_API_KEYS.PER_VM_CPU_USAGE]:
    'metrics/api/v1/query_range?query=pcd:vm_cpu_usage{domain="{{id}}"}',
  [METRICS_API_KEYS.PER_VM_MEMORY_USAGE]:
    'metrics/api/v1/query_range?query=pcd:vm_mem_usage{domain="{{id}}"}',
  [METRICS_API_KEYS.PER_VM_CPU_THROTTLE]:
    'metrics/api/v1/query_range?query=pcd:vm_cpu_throttling_percentage{domain="{{id}}"}',
  [METRICS_API_KEYS.PER_VM_DISK_READ_THROUGHPUT]:
    'metrics/api/v1/query_range?query=pcd:vm_read_throughput{domain="{{id}}"}',
  [METRICS_API_KEYS.PER_VM_DISK_WRITE_THROUGHPUT]:
    'metrics/api/v1/query_range?query=pcd:vm_write_throughput{domain="{{id}}"}',
  [METRICS_API_KEYS.PER_VM_DISK_READ_IOPS]:
    'metrics/api/v1/query_range?query=pcd:vm_read_iops{domain="{{id}}"}',
  [METRICS_API_KEYS.PER_VM_DISK_WRITE_IOPS]:
    'metrics/api/v1/query_range?query=pcd:vm_write_iops{domain="{{id}}"}',
  [METRICS_API_KEYS.PER_VM_NETWORK_RX_THROUGHPUT]:
    'metrics/api/v1/query_range?query=pcd:vm_rx_bytes{domain="{{id}}"}',
  [METRICS_API_KEYS.PER_VM_NETWORK_TX_THROUGHPUT]:
    'metrics/api/v1/query_range?query=pcd:vm_tx_bytes{domain="{{id}}"}',
}

export const TREND_STEPS = 10

function getTimeRange(option: string) {
  const now = new Date() // current time
  const end = now.getTime() / 1000 // end time in seconds

  let start
  switch (option) {
    case TIME_RANGES.last_5_minutes:
      start = new Date(now.getTime() - 5 * 60 * 1000)
      break
    case TIME_RANGES.last_15_minutes:
      start = new Date(now.getTime() - 15 * 60 * 1000)
      break
    case TIME_RANGES.last_30_minutes:
      start = new Date(now.getTime() - 30 * 60 * 1000)
      break
    case TIME_RANGES.last_1_hour:
      start = new Date(now.getTime() - 1 * 60 * 60 * 1000)
      break
    case TIME_RANGES.last_3_hours:
      start = new Date(now.getTime() - 3 * 60 * 60 * 1000)
      break
    case TIME_RANGES.last_6_hours:
      start = new Date(now.getTime() - 6 * 60 * 60 * 1000)
      break
    case TIME_RANGES.last_12_hours:
      start = new Date(now.getTime() - 12 * 60 * 60 * 1000)
      break
    case TIME_RANGES.last_24_hours:
      start = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      break
    case TIME_RANGES.last_48_hours:
      start = new Date(now.getTime() - 48 * 60 * 60 * 1000)
      break
    case TIME_RANGES.last_7_days:
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      break
    case TIME_RANGES.last_30_days:
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      break
    case TIME_RANGES.last_60_days:
      start = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)
      break
    default:
      throw new Error('Invalid time range option')
  }

  const startTimestamp = start.getTime() / 1000 // in seconds

  return {
    start: startTimestamp,
    end: end,
    step: Math.floor((end * 1000 - startTimestamp * 1000) / TREND_STEPS),
  }
}

function convertUnixTimestampToLocalTime(timestamp: number, option): string {
  const date = new Date(timestamp * 1000)
  switch (option) {
    case TIME_RANGES.last_5_minutes:
    case TIME_RANGES.last_15_minutes:
    case TIME_RANGES.last_30_minutes:
    case TIME_RANGES.last_1_hour:
    case TIME_RANGES.last_3_hours:
    case TIME_RANGES.last_6_hours:
    case TIME_RANGES.last_12_hours:
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
    case TIME_RANGES.last_24_hours:
    case TIME_RANGES.last_48_hours:
    case TIME_RANGES.last_7_days:
    case TIME_RANGES.last_30_days:
    case TIME_RANGES.last_60_days:
      return `${date.getMonth() + 1}/${date.getDate()} ${date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })}`
    default:
      return date.toLocaleString()
  }
}

function formatMetricValue(value: number, key: string): number {
  let metricValue = value ? value : 0

  // If metric value is 0, no need to format it
  if (metricValue === 0) {
    return 0
  }

  switch (key) {
    case METRICS_API_KEYS.ALL_HOSTS_CPU_USAGE:
    case METRICS_API_KEYS.PER_HOST_CPU_USAGE:
    case METRICS_API_KEYS.SELECTED_HOST_CPU_USAGE:
    case METRICS_API_KEYS.SELECTED_VMS_CPU_USAGE:
      return Number(Number(metricValue).toFixed(3))
    case METRICS_API_KEYS.ALL_HOSTS_MEMORY_USAGE:
    case METRICS_API_KEYS.PER_HOST_MEMORY_USAGE:
    case METRICS_API_KEYS.ALL_VMS_MEMORY_USAGE:
    case METRICS_API_KEYS.PER_VM_MEMORY_USAGE:
    case METRICS_API_KEYS.SELECTED_VMS_MEMORY_USAGE:
    case METRICS_API_KEYS.SELECTED_HOST_MEMORY_USAGE:
      return Number(Number((metricValue / 1024).toFixed(3))) // Convert KB to GB
    case METRICS_API_KEYS.ALL_HOSTS_NETWORK_RX_THROUGHPUT:
    case METRICS_API_KEYS.ALL_HOSTS_NETWORK_TX_THROUGHPUT:
    case METRICS_API_KEYS.PER_HOST_NETWORK_RX_THROUGHPUT:
    case METRICS_API_KEYS.PER_HOST_NETWORK_TX_THROUGHPUT:
    case METRICS_API_KEYS.ALL_VMS_NETWORK_RX_THROUGHPUT:
    case METRICS_API_KEYS.ALL_VMS_NETWORK_TX_THROUGHPUT:
    case METRICS_API_KEYS.PER_VM_NETWORK_RX_THROUGHPUT:
    case METRICS_API_KEYS.PER_VM_NETWORK_TX_THROUGHPUT:
    case METRICS_API_KEYS.SELECTED_VMS_NETWORK_RX_THROUGHPUT:
    case METRICS_API_KEYS.SELECTED_VMS_NETWORK_TX_THROUGHPUT:
      // Convert Bytes per second to Kbps
      // 1 Byte = 8 bits, 1 Kbps = 1,000 bits per second
      // So Bytes/s to Kbps = (Bytes/s * 8) / 1,000 = Bytes/s / 125
      return Number(Number((metricValue / 125).toFixed(3)))
    default:
      return Number(Number(metricValue).toFixed(3))
  }
}

interface UseTrendDataProps {
  metricKey: string
  currentTimeRange: string
  params?: Record<string, any>
  resources?: string[]
}

/**
 * @summary Custom hook to fetch trend data from metrics API and format it for display as per the key
 * @param {string} param0.metricKey - The key to fetch trend data for
 * @param {string} param0.currentTimeRange - The current time range to fetch trend data for
 * @returns {Object} - The trend data
 */
const useTrendData = ({
  metricKey,
  currentTimeRange,
  params = {},
  resources = [],
}: UseTrendDataProps) => {
  const [values, setValues] = useState([])
  const [average, setAverage] = useState<number>(0)

  if (!metricKey) {
    return {
      values: [],
      average: 0,
    }
  }

  // Get base URL for metrics API
  const metricsEndpoint = useMemo(() => {
    if (isDevelopment() || isLocalhost()) return config.apiHost

    return window.location.origin
  }, [])

  // Create URL with params
  const urlWithParams = useMemo(() => {
    const { start, end, step } = getTimeRange(currentTimeRange)

    // Replace params in URL with params from props
    let baseURL = `${metricsEndpoint}/${METRICS_ENDPOINTS[metricKey]}`
    Object.entries(params)?.forEach(
      ([key, value]) => (baseURL = baseURL.replace(`{{${key}}}`, value)),
    )

    // Add time range params
    return `${baseURL}&start=${start}&end=${end}&step=${step}ms`
  }, [currentTimeRange, params, metricsEndpoint, metricKey])

  /**
   * Aggregates usage data based on a list of hosts to filter
   * @param hostList - Array of host IDs to filter the data by
   * @param usageData - The usage data object from the metrics API or dummyData
   * @returns An object with timestamps as keys and their corresponding values
   */
  const aggregateUsageDataByHosts = (
    hostList: string[],
    usageData: any,
  ): Record<string, string> => {
    // Initialize the result object
    const aggregatedData: Record<string, string> = {}

    // Filter results based on the host list
    const filteredResults =
      usageData?.result?.filter((item: any) => {
        return hostList.includes(item.metric?.host) || hostList.includes(item.metric?.domain)
      }) || []

    // Process each filtered result
    filteredResults?.forEach((result: any) => {
      const values = result.values || []

      // For each timestamp-value pair in the current host's data
      values?.forEach((valueItem: [number, string]) => {
        const timestamp = valueItem[0]
        const value = valueItem[1]

        if (aggregatedData[timestamp]) {
          // Sum the values (converting from string to number and back)
          const existingValue = parseFloat(aggregatedData[timestamp])
          const newValue = parseFloat(value)
          aggregatedData[timestamp] = (existingValue + newValue).toString()
        } else {
          // First occurrence of this timestamp
          aggregatedData[timestamp] = value
        }
      })
    })

    return aggregatedData
  }

  /**
   * Formats the aggregated data for display
   * @param data - The aggregated data object
   */
  const formatAggregatedData = (data: Record<string, string>) => {
    let avg = 0
    const values = Object.keys(data).map((key) => {
      avg += Number(data[key])
      return {
        name: convertUnixTimestampToLocalTime(Number(key), currentTimeRange),
        value: formatMetricValue(Number(data[key]), metricKey),
      }
    })
    setAverage(formatMetricValue(Number(avg / values.length), metricKey))
    setValues(values)
  }

  /**
   * Fetches metrics data from metrics API
   */
  const getMetricsData = async () => {
    // If metric key is for selected resource and no resources are selected, return
    if (SHOW_FOR_SELECTED_RESOURCE_KEYS.includes(metricKey) && !resources?.length) {
      setValues([])
      setAverage(0)
      return
    }

    await fetch(urlWithParams)
      .then((response) => response.json())
      .then(({ data }) => {
        // Aggregate usage data by hosts if metric key is for selected resource
        if (SHOW_FOR_SELECTED_RESOURCE_KEYS.includes(metricKey)) {
          const aggregatedData = aggregateUsageDataByHosts(resources || [], data)
          formatAggregatedData(aggregatedData)
          return
        }

        const aggregatedData: Record<string, string> = {}
        data?.result?.[0]?.values?.forEach((valueItem: any) => {
          const timestamp = valueItem[0]
          const value = valueItem[1]
          aggregatedData[timestamp] = value
        })
        formatAggregatedData(aggregatedData)
      })
      .catch((error) => {
        console.error('Error fetching metrics:', error)
      })
  }

  const reload = useCallback(() => {
    getMetricsData()
  }, [getMetricsData])

  // Fetch trends data from metrics API
  useEffect(() => {
    getMetricsData()
  }, [currentTimeRange])

  return {
    values,
    average,
    reload,
  }
}

export default useTrendData
